import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** Supports both controlled and uncontrolled usage of a value. */
export function useControllableState<T>(options: {
  value?: T;
  defaultValue: T;
  onChange?: (value: T) => void;
}): [T, (next: T | ((prev: T) => T)) => void] {
  const { value, defaultValue, onChange } = options;
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<T>(defaultValue);
  const current = isControlled ? (value as T) : internal;
  const latest = useRef({ current, onChange });
  useLayoutEffect(() => {
    latest.current = { current, onChange };
  });

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const prev = latest.current.current;
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(prev) : next;
      if (!isControlled) setInternal(resolved);
      if (!Object.is(resolved, prev)) latest.current.onChange?.(resolved);
      latest.current = { ...latest.current, current: resolved };
    },
    [isControlled],
  );
  return [current, set];
}
